import React, { useState } from "react";
import { Button, notification, Input, Space, Collapse } from "antd";
import { TagOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useAppStore } from "../../store";
import { convertToInternationalFormat, getCouponErrorMessage } from "../../utils";
import DataProvider from "../../services";
import PhoneNumberInput from "../../components/form/phone-number";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import mtnLogo from "../../assets/images/mtn.png";

const { Panel } = Collapse;

export default function Mcarebilling(props: any) {
  const [api, contextHolder] = notification.useNotification();
  const { plan } = useAppStore();
  const [number, setNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [couponInput, setCouponInput] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleApplyCoupon = async () => {
    const code = couponInput?.trim().toUpperCase();
    if (!code) {
      api.warning({ message: "Coupon", description: "Enter a coupon code." });
      return;
    }
    setValidatingCoupon(true);
    try {
      const result = await DataProvider.validateCoupon(code);
      if (result.valid) {
        setAppliedCoupon(code);
        api.success({
          message: "Coupon applied",
          description: `"${code}" will be applied when you proceed.`,
        });
      } else {
        api.error({
          message: "Invalid coupon",
          description: result.message ?? "This coupon is not valid.",
        });
      }
    } catch {
      api.error({
        message: "Invalid coupon",
        description:
          "Could not validate coupon. Please check the code and try again.",
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  async function onRequestSub() {
    if (!number || number.length > 10 || number.length < 10) {
      return api["warning"]({
        message: "Warning",
        description: "Phone number is required.",
      });
    }

    if (!number.startsWith("0")) {
      return api["warning"]({
        message: "Warning",
        description: "Phone number must start with 0.",
      });
    }

    if (plan?.id == null) {
      return api["error"]({
        message: "Error",
        description: "Plan not selected. Please go back and choose a plan.",
      });
    }

    const payload: { mcareId: number; msisdn: string; couponCode?: string } = {
      mcareId: plan.id,
      msisdn: convertToInternationalFormat(number),
    };
    if (appliedCoupon) payload.couponCode = appliedCoupon;

    try {
      setLoading(true);
      const { data } = await DataProvider.requestSubscription(payload);
      const isCouponSuccess =
        data?.statusMessage &&
        String(data.statusMessage).toLowerCase().includes("coupon");
      setLoading(false);
      if (isCouponSuccess) {
        navigate("/main/plan/mcare-comfirm-pay-00725?success=coupon");
      } else {
        navigate("/main/plan/mcare-comfirm-pay-00725");
      }
    } catch (error: any) {
      setLoading(false);
      if (appliedCoupon) {
        return api["error"]({
          message: "Coupon",
          description: getCouponErrorMessage(error),
        });
      }
      if (axios.isAxiosError(error)) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          const errorMessage = error.response.data.message;
          return api["error"]({
            message: "Failed",
            description:
              errorMessage === "Offer does not exist"
                ? "The subscription offer selected does not exist."
                : errorMessage === "Duplicate Request"
                ? "Too many requests, kindly try again 10 minutes later."
                : errorMessage,
          });
        } else {
          return api["error"]({
            message: "Failed",
            description:
              error.message === "Offer does not exist"
                ? "The subscription offer selected does not exist."
                : error.message === "Duplicate Request"
                ? "Too many requests, kindly try again 10 minutes later."
                : error.message,
          });
        }
      } else {
        return api["error"]({
          message: "Failed",
          description: "Subscription payment failed, try again.",
        });
      }
    }
  }

  const offersContent = plan?.offers?.map((offer, idx) => (
    <div key={idx}>
      <p className="text-sm font-semibold text-neutral-800 mb-1">
        {offer.name}
      </p>
      <ul className="space-y-1">
        {offer.list.map((item, idxx) => (
          <li
            key={idxx}
            className="flex items-start gap-2 text-sm text-neutral-600"
          >
            <CheckCircleFilled className="text-[#1A94EC] mt-0.5 text-xs shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  ));

  return (
    <div className="w-full min-h-[70vh] bg-white border border-neutral-200 mt-4 rounded-2xl">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            Complete your subscription
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Review your plan and pay to activate Mcare.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-6">
          {/* Order summary */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 bg-gradient-to-br from-[#1A94EC] to-[#0d7bc4] text-white">
                <p className="text-xs uppercase tracking-wide opacity-80">
                  Selected plan
                </p>
                <h2 className="text-xl font-bold mt-1">
                  {plan?.description ?? "—"}
                </h2>
                <p className="mt-2 text-2xl font-bold">
                  GH¢{plan?.price ?? 0}
                  <span className="text-sm font-normal opacity-80">
                    /month
                  </span>
                </p>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Billing period</span>
                  <span className="font-medium text-neutral-900">
                    1 month
                  </span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Coupon</span>
                    <span className="font-medium text-green-600">
                      {appliedCoupon}
                    </span>
                  </div>
                )}
                <div className="pt-2.5 mt-1 border-t border-neutral-100 flex justify-between items-baseline">
                  <span className="text-sm font-medium text-neutral-900">
                    Total due today
                  </span>
                  <span className="text-lg font-bold text-neutral-900">
                    GH¢{plan?.price ?? 0}
                  </span>
                </div>
              </div>
              {plan?.offers && plan.offers.length > 0 && (
                <>
                  {/* Desktop: sits in its own column next to the payment
                      form, so there's no cost to always showing it. */}
                  <div className="hidden lg:block px-5 pb-5 pt-1 border-t border-neutral-100">
                    <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 mt-3">
                      What&apos;s included
                    </p>
                    <div className="space-y-3">{offersContent}</div>
                  </div>
                  {/* Mobile: stacked above the payment form, so this stays
                      collapsed by default - otherwise a long benefits list
                      pushes "Mobile Money" well below the fold. */}
                  <div className="lg:hidden border-t border-neutral-100">
                    <Collapse ghost expandIconPosition="end">
                      <Panel
                        header={
                          <span className="text-sm font-semibold text-neutral-700">
                            What&apos;s included
                          </span>
                        }
                        key="1"
                      >
                        <div className="space-y-3">{offersContent}</div>
                      </Panel>
                    </Collapse>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Payment form */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-neutral-100">
                <img src={mtnLogo} alt="MTN" className="h-8 w-8 object-contain shrink-0" />
                <div>
                  <h3 className="font-semibold text-neutral-900">
                    Mobile Money
                  </h3>
                  <p className="text-xs text-neutral-500">
                    Pay securely via MTN Mobile Money
                  </p>
                </div>
              </div>

              <PhoneNumberInput
                required
                title="Enter Phone Number"
                value={number}
                onChangeText={setNumber}
                placeholder="eg:0240005678"
              />

              <div className="mb-5 mt-4">
                <label
                  htmlFor="mcare-billing-coupon-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <TagOutlined className="mr-1" aria-hidden />
                  Have a coupon?
                </label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                    <span className="text-sm font-medium text-green-800">
                      {appliedCoupon}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      onClick={handleRemoveCoupon}
                      className="text-gray-600 p-0 h-auto"
                      aria-label="Remove coupon"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Space.Compact className="w-full">
                    <Input
                      id="mcare-billing-coupon-input"
                      placeholder="Enter coupon code"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onPressEnter={handleApplyCoupon}
                      className="rounded-l-lg rounded-r-none"
                      maxLength={64}
                      aria-label="Coupon code"
                    />
                    <Button
                      type="default"
                      onClick={handleApplyCoupon}
                      loading={validatingCoupon}
                      className="rounded-r-lg rounded-l-none shrink-0"
                      aria-label="Apply coupon"
                    >
                      Apply
                    </Button>
                  </Space.Compact>
                )}
              </div>

              <p className="text-xs text-neutral-500 mb-5">
                By clicking &quot;Proceed with payment&quot; you agree to the
                terms of service of the company.
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <Button
                  onClick={() => navigate("/main/plans")}
                  size="large"
                  className="w-full sm:w-auto"
                >
                  Exit
                </Button>
                <Button
                  onClick={onRequestSub}
                  type="primary"
                  size="large"
                  loading={loading}
                  disabled={loading}
                  style={{ backgroundColor: "#1A94EC" }}
                  className="w-full sm:flex-1"
                >
                  {loading ? "Processing..." : "Proceed to payment"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {contextHolder}
    </div>
  );
}
